import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { startOfDay } from 'date-fns';
import { z } from 'zod';
import { authenticatedRoute } from '@/authenticated-route';
import FullPageLoadingSpinner from '@/components/ui/full-page-loading-spinner';
import { SHIFT_TYPES } from '@/consts/app-config';
import { getSuspenseQueryMyStationOptions } from '@/features/my-patient/services/use-query-my-station';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import * as formSystemData from '@/shared/form-system/data';
import * as formSystemSchema from '@/shared/form-system/schema';
import { InisPatientCardSource } from './components/patient-card-source';
import { VIEW_MODES, VIEW_TO_LAYOUT } from './consts/daily-care-view-mode';
import { INIS_LAYOUT_ROUTE_ID, INIS_ROUTES } from './consts/routes';
import {
  type DailyCareHomeQueryParams,
  getSuspenseQueryDailyCareHomeOptions,
} from './services/use-query-daily-care-home';
import { TASK_TYPES } from './types/task-overview';

const myPatientSearchSchema = z.object({
  stations: z.array(z.string()).optional(),
  selectedPatientId: z.string().optional(),
  mrn: z.string().optional(),
  encounterId: z.string().optional(),
  tab: z
    .enum(['patient-care', 'task-overview'])
    .optional()
    .default('patient-care'),
  overviewDate: z
    .string()
    .optional()
    .default(() => new Date().toISOString()),
  overviewShift: z.enum(SHIFT_TYPES).optional(),
  overviewFilters: z.array(z.enum(TASK_TYPES)).optional(),
});

export type MyPatientSearchParams = z.infer<typeof myPatientSearchSchema>;

const myPatientRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: INIS_ROUTES.index,
  validateSearch: zodValidator(myPatientSearchSchema),
  beforeLoad: beforeLoadRouterGuard([], { featureFlag: 'myPatientEnabled' }),
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(getSuspenseQueryMyStationOptions());
  },
  staticData: {
    patientCardSource: InisPatientCardSource,
  },
}).lazy(() => import('./my-patient').then(({ Route }) => Route));

const inisLayoutSearchSchema = z.object({
  mrn: z.string(),
  encounterId: z.string(),
});

const inisLayoutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  id: INIS_LAYOUT_ROUTE_ID,
  validateSearch: zodValidator(inisLayoutSearchSchema),
  staticData: {
    patientCardSource: InisPatientCardSource,
  },
}).lazy(() => import('./components/inis-layout').then(({ Route }) => Route));

const dailyCareSearchSchema = z.object({
  date: z
    .string()
    .optional()
    .default(() => new Date().toISOString()),
  view: z.enum(VIEW_MODES).optional().default('shift'),
});

type DailyCareHomeSearchParams = z.infer<typeof dailyCareSearchSchema>;

const toDailyCareHomeQueryParams = ({
  encounterId,
  date,
  view,
}: DailyCareHomeSearchParams & {
  encounterId: string;
}): DailyCareHomeQueryParams => ({
  encounterId,
  targetDatetime: startOfDay(new Date(date)).toISOString(),
  layout: VIEW_TO_LAYOUT[view],
});

const dailyCareRoute = createRoute({
  getParentRoute: () => inisLayoutRoute,
  path: INIS_ROUTES.dailyCare,
  validateSearch: zodValidator(dailyCareSearchSchema),
  beforeLoad: beforeLoadRouterGuard([], { featureFlag: 'dailyCareEnabled' }),
  loaderDeps: ({ search: { date, encounterId, view } }) => {
    return toDailyCareHomeQueryParams({ date, encounterId, view });
  },
  loader: ({ deps, context: { queryClient } }) => {
    return queryClient.ensureQueryData(
      getSuspenseQueryDailyCareHomeOptions(deps),
    );
  },
  pendingComponent: FullPageLoadingSpinner,
  staticData: {
    parent: INIS_ROUTES.index,
  },
}).lazy(() => import('./daily-care').then(({ Route }) => Route));

const patientEducationRoute = createRoute({
  getParentRoute: () => inisLayoutRoute,
  path: INIS_ROUTES.patientEducation,
  beforeLoad: beforeLoadRouterGuard([], {
    featureFlag: 'patientEducationEnabled',
  }),
  loaderDeps: ({ search: { encounterId } }) => ({ encounterId }),
  loader: async ({ deps, context: { queryClient } }) => {
    await queryClient.ensureQueryData(
      formSystemData.getSuspenseQueryPatientEducationOptions(deps),
    );
  },
  pendingComponent: FullPageLoadingSpinner,
  staticData: {
    parent: INIS_ROUTES.index,
  },
}).lazy(() => import('./patient-education').then(({ Route }) => Route));

const assessmentFormsSearchSchema = z
  .object({
    runtimeId: z.string().optional(),
    rowType: z.lazy(() => formSystemSchema.runtimeRowTypeSchema).optional(),
    templateId: z.string().optional(),
  })
  .refine((data) => !(data.templateId && data.runtimeId), {
    message: 'templateId and runtimeId are mutually exclusive',
  })
  .refine((data) => !data.runtimeId || !!data.rowType, {
    message: 'runtimeId requires rowType',
  });

const assessmentFormsRoute = createRoute({
  getParentRoute: () => inisLayoutRoute,
  path: INIS_ROUTES.assessmentForms,
  validateSearch: zodValidator(assessmentFormsSearchSchema),
  staticData: {
    parent: INIS_ROUTES.index,
  },
}).lazy(() => import('./assessment-forms').then(({ Route }) => Route));

export const inisRoutes = [
  myPatientRoute,
  inisLayoutRoute.addChildren([
    dailyCareRoute,
    assessmentFormsRoute,
    patientEducationRoute,
  ]),
] as const;
